import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useTheme } from "next-themes";
import { F, th as palette, Over, Btn } from "@/lib/bricks";
import { Page, PageHeader, Stats, StatusTabs, Controls, Toolbar, SmallSelect, List, Row, Empty, TextLink, Loading, figLabel } from "@/components/calm/Calm";
import { toast } from "sonner";

function getTier(agent) {
  const alloc = agent.monthly_allocation || 0;
  if (alloc === 0) return 'dormant';
  const pct = (agent.shoots_used / alloc) * 100;
  if (pct >= 90) return 'heavy';
  if (pct >= 50) return 'regular';
  if (pct >= 10) return 'light';
  return 'dormant';
}

export default function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [editAgent, setEditAgent] = useState(null);
  const [allocation, setAllocation] = useState("");
  const [addAgentModal, setAddAgentModal] = useState(false);
  const [inviteLinkModal, setInviteLinkModal] = useState(false);
  const [newAgent, setNewAgent] = useState({ name: '', email: '', allocation: 10 });
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const loadAgents = () => {
    api.get("/agents").then(r => { setAgents(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { loadAgents(); }, []);

  const handleUpdateAllocation = async () => {
    if (!editAgent || !allocation) return;
    try {
      await api.put(`/agents/${editAgent.agent_id}/allocation`, { monthly_allocation: parseInt(allocation) });
      toast.success("Allocation updated"); setEditAgent(null); loadAgents();
    } catch { toast.error("Failed to update allocation"); }
  };

  const handleAddAgent = async () => {
    if (!newAgent.email || !newAgent.name) return toast.error("Name and email are required");
    try {
      await api.post("/agents/invite", newAgent);
      toast.success(`Invite sent to ${newAgent.email}`);
      setAddAgentModal(false);
      setNewAgent({ name: '', email: '', allocation: 10 });
      loadAgents();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add agent"); }
  };

  const filtered = agents.filter(agent => {
    const tier = getTier(agent);
    if (activeTab === 'All') return true;
    if (activeTab === 'Active') return agent.status === 'active' && tier !== 'dormant';
    if (activeTab === 'At cap') return tier === 'heavy';
    if (activeTab === 'Light') return tier === 'light';
    if (activeTab === 'Dormant') return tier === 'dormant';
    if (activeTab === 'Invited') return agent.status === 'invited' || agent.status === 'pending';
    return true;
  });

  if (loading) return <Loading />;

  const totalAllocated = agents.reduce((s, a) => s + (a.monthly_allocation || 0), 0);
  const totalConsumed  = agents.reduce((s, a) => s + (a.shoots_used || 0), 0);
  const atOrOverCap    = agents.filter(a => getTier(a) === 'heavy').length;
  const dormantCount   = agents.filter(a => getTier(a) === 'dormant').length;
  const TIER_TEXT = { heavy: 'At cap', regular: 'Regular', light: 'Light', dormant: 'Dormant' };
  const mainTab = ['All', 'Active', 'At cap', 'Invited'].includes(activeTab) ? activeTab : 'All';

  return (
    <Page testid="agents-page">
      <PageHeader
        title="Agents"
        subtitle={`${agents.length} agents, ${agents.filter(a => a.status === 'active').length} active`}
        action={
          <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextLink testid="agents-invite-link" onClick={() => setInviteLinkModal(true)}>Invite link</TextLink>
            <Btn variant="primary" dark={dark} icon="plus" onClick={() => setAddAgentModal(true)}>Add agent</Btn>
          </div>
        }
      />

      <Stats items={[
        { label: `Shoots used of ${totalAllocated}`, value: totalConsumed },
        { label: 'At or over cap', value: atOrOverCap, attention: atOrOverCap > 0 },
        { label: 'Dormant', value: dormantCount },
      ]} />

      <Controls>
        <StatusTabs testid="agents-status-filter" value={mainTab} onChange={setActiveTab}
          options={['All', 'Active', 'At cap', 'Invited'].map(k => ({ key: k, label: k }))} />
        <Toolbar>
          <SmallSelect testid="agents-usage" label="Usage" value={['Light', 'Dormant'].includes(activeTab) ? activeTab : 'any'}
            onChange={(k) => setActiveTab(k === 'any' ? 'All' : k)}
            options={[{ key: 'any', label: 'Any usage' }, { key: 'Light', label: 'Light' }, { key: 'Dormant', label: 'Dormant' }]} />
        </Toolbar>
      </Controls>

      {filtered.length === 0 ? (
        <Empty action={<TextLink onClick={() => setAddAgentModal(true)}>Add agent</TextLink>}>
          {agents.length === 0 ? 'No agents yet. Add one or share your invite link.' : 'No agents in this view.'}
        </Empty>
      ) : (
        <List testid="agents-list">
          {filtered.map(agent => {
            const tier = getTier(agent);
            const invited = agent.status === 'invited' || agent.status === 'pending';
            return (
              <Row
                key={agent.agent_id || agent.user_id || agent.email}
                testid="agent-row"
                title={agent.name}
                meta={[agent.email]}
                next={agent.monthly_allocation != null ? [figLabel(`${agent.shoots_used ?? 0} of ${agent.monthly_allocation}`, 'shoots this month')] : null}
                status={invited ? 'Invited' : TIER_TEXT[tier]}
                attention={!invited && tier === 'heavy'}
                aside={<TextLink testid="agent-allocation" onClick={() => { setEditAgent(agent); setAllocation(String(agent.monthly_allocation)); }}>Adjust</TextLink>}
              />
            );
          })}
        </List>
      )}

      {/* Edit allocation dialog */}
      <Dialog open={!!editAgent} onOpenChange={() => setEditAgent(null)}>
        <DialogContent style={{ maxWidth: 360, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 17, fontWeight: 500, letterSpacing: -0.3, color: p.ink }}>Adjust allocation</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Monthly shoots for {editAgent?.name}
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 20 }}>
            <div>
              <Over color={p.ink3}>Current usage</Over>
              <div style={{ fontFamily: F.mono, fontSize: 15, marginTop: 10, color: p.ink }}>{editAgent?.shoots_used} / {editAgent?.monthly_allocation} shoots</div>
            </div>
            <div>
              <Over color={p.ink3}>New allocation</Over>
              <input
                type="number" value={allocation}
                onChange={e => setAllocation(e.target.value)}
                min={1}
                style={{ marginTop: 10, width: '100%', padding: '12px 14px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 15, fontFamily: F.mono, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" onClick={() => setEditAgent(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleUpdateAllocation}>Save changes</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Agent Modal */}
      <Dialog open={addAgentModal} onOpenChange={setAddAgentModal}>
        <DialogContent style={{ maxWidth: 400, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, letterSpacing: -0.3, color: p.ink }}>Add agent</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              They get an email invite to join your office.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 20 }}>
            <div>
              <Over color={p.ink3}>Full name</Over>
              <input
                type="text" value={newAgent.name}
                onChange={e => setNewAgent({ ...newAgent, name: e.target.value })}
                placeholder="Full name"
                style={{ marginTop: 10, width: '100%', padding: '12px 14px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <Over color={p.ink3}>Email address</Over>
              <input
                type="email" value={newAgent.email}
                onChange={e => setNewAgent({ ...newAgent, email: e.target.value })}
                placeholder="marisol@example.com"
                style={{ marginTop: 10, width: '100%', padding: '12px 14px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <Over color={p.ink3}>Monthly allocation</Over>
              <input
                type="number" value={newAgent.allocation}
                onChange={e => setNewAgent({ ...newAgent, allocation: parseInt(e.target.value) })}
                style={{ marginTop: 10, width: '100%', padding: '12px 14px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <DialogFooter style={{ marginTop: 32, gap: 10 }}>
            <Btn variant="ghost" onClick={() => setAddAgentModal(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleAddAgent}>Add agent</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Link Modal */}
      <Dialog open={inviteLinkModal} onOpenChange={setInviteLinkModal}>
        <DialogContent style={{ maxWidth: 400, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, letterSpacing: -0.3, color: p.ink }}>Invite link</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Share this link so your team can join the office.
            </DialogDescription>
          </DialogHeader>
          <div style={{ marginTop: 24 }}>
            <Over color={p.ink3}>Registration link</Over>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
               <div style={{ flex: 1, padding: '12px 14px', border: `1px solid ${p.hairS}`, borderRadius: 2, background: p.surf2, color: p.ink3, fontSize: 13, fontFamily: F.mono, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                 {window.location.origin}/login
               </div>
               <Btn variant="ghost" onClick={() => { navigator.clipboard.writeText(window.location.origin + '/login'); toast.success("Copied to clipboard"); }}>Copy</Btn>
            </div>
          </div>
          <DialogFooter style={{ marginTop: 32 }}>
            <Btn variant="primary" full onClick={() => setInviteLinkModal(false)}>Done</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
